/** Recording formats ordered by browser ecosystem preference. */
const RECORDING_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
  "audio/webm",
  "audio/ogg;codecs=opus",
] as const;

/** Selects the first format supported by the current MediaRecorder runtime. */
export function selectRecordingMimeType(
  isTypeSupported: (mimeType: string) => boolean,
): string | undefined {
  return RECORDING_MIME_TYPES.find((mimeType) => isTypeSupported(mimeType));
}

/** Returns the filename suffix that matches the recorded container. */
export function recordingFileExtension(mimeType: string): string {
  const container = mimeType.split(";", 1)[0]?.toLowerCase();
  if (container === "audio/mp4") return "m4a";
  if (container === "audio/ogg") return "ogg";
  if (container === "audio/mpeg") return "mp3";
  if (container === "audio/wav" || container === "audio/x-wav") return "wav";
  return "webm";
}

/** Converts MIME/container details into the stable codec field used by IM v3. */
export function normalizedAudioCodec(mimeType: string): string | undefined {
  const normalized = mimeType.toLowerCase();
  if (normalized.includes("opus")) return "opus";
  if (normalized.includes("mp4a") || normalized.startsWith("audio/mp4")) return "aac";
  if (normalized.startsWith("audio/mpeg")) return "mp3";
  if (normalized.includes("wav")) return "pcm";
  const subtype = normalized.split(";", 1)[0]?.split("/", 2)[1]?.slice(0, 32) ?? "";
  return subtype.length === 0 ? undefined : subtype;
}

/** Maps browser-specific microphone exceptions to an actionable Chinese prompt. */
export function microphoneErrorMessage(cause: unknown): string {
  if (cause instanceof DOMException) {
    switch (cause.name) {
      case "NotAllowedError":
      case "PermissionDeniedError":
        return "麦克风权限被系统拒绝，请在浏览器地址栏和系统设置中允许麦克风后重试";
      case "NotFoundError":
      case "DevicesNotFoundError":
        return "未检测到可用麦克风，请连接麦克风后重试";
      case "NotReadableError":
      case "TrackStartError":
        return "麦克风正被其他应用占用，或被系统隐私设置禁用";
      case "SecurityError":
        return "当前页面无法安全访问麦克风，请使用 HTTPS 打开";
      case "AbortError":
        return "麦克风启动被中断，请重试";
      default:
        break;
    }
  }
  return cause instanceof Error ? cause.message : "无法启动录音，请检查麦克风权限";
}
